import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import Accordion from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import ArrowUp from "@insite/mobius/Icons/ArrowUp";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CompareActive from "@tIntermatic/Icons/CompareActive";
import CompareDefault from "@tIntermatic/Icons/CompareDefault";
import MenuDownArrow from "@tIntermatic/Icons/MenuDownArrow";
import Plus from "@tIntermatic/Icons/Plus";
import React, { FC, useState } from "react";
import { css } from "styled-components";

export interface FilterTypeAccordionSectionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
    optionsCss?: InjectableCss;
    wrapper?: InjectableCss;
    itemName?: InjectableCss;
}

export const filterTypeAccordionSectionStyles: FilterTypeAccordionSectionStyles = {
    wrapper: {
        css: css`
            border-bottom: 2px solid rgba(0, 0, 0, 0.1);
            width: 100%;
        `,
    },
    accordionSection: {
        toggleIconProps: {
            src: MenuDownArrow,
            size: 11,
            css: css`
                transform: rotate(180deg);
                > svg {
                    > g {
                        stroke-width: 0.8;
                    }
                }
            `,
        },

        expandIconProps: {
            src: MenuDownArrow,
            size: 11,
            css: css`
                > svg {
                    > g {
                        stroke-width: 0.8;
                    }
                }
            `,
        },

        headerProps: {
            css: css`
                > button {
                    background: #fff;
                    border: none;
                    padding: 16px 9px 13px 1px !important;
                    > span {
                        font-size: 18px;
                        font-weight: 600;
                        letter-spacing: 0;
                        line-height: 22px;
                        color: #333333;
                        @media all and (max-width: 992px) and (min-width: 0px) {
                            font-size: 16px;
                            font-weight: 600;
                            letter-spacing: 0;
                            line-height: 19px;
                        }
                    }
                }
            `,
        },

        panelProps: {
            css: css`
                border: none;
                overflow-y: auto;
                overflow-x: hidden;
                max-height: 350px;
                padding: 0 4px 20px 0;
            `,
        },
    },
    checkBoxGroup: {
        css: css`
            width: 100%;
            height: auto;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            &:not(:last-child) {
                margin: 7px 0px;
            }
            span[role=checkbox] + label{
                line-height: 1.2;
            }
        `,
    },
    checkBox: {
        iconProps: { src: CompareDefault },
        css: css`
            span[class^="IconWrapper"] {
                min-width: 19px;
                min-height: 19px;
                > svg {
                    height: 19px;
                    width: 19px;
                }
                &:focus {
                    box-shadow: none;
                }
            }
        `,
    },
    checkBoxSelected: {
        iconProps: { src: CompareActive },
        css: css`
            span[class^="IconWrapper"] {
                min-width: 19px;
                min-height: 19px;
                > svg {
                    height: 19px;
                    width: 19px;
                }
                &:focus {
                    box-shadow: none;
                }
            }
        `,
    },
    seeAllLink: {
        css: css`
            padding-top: 12px;
            font-size: 14px;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 17px;
            color: #006bc0;
        `,
    },
    seeLessLink: {
        css: css`
            padding-top: 12px;
            font-size: 14px;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 17px;
            color: #006bc0;
        `,
    },

    itemName: {
        css: css`
            font-size: 13px;
            letter-spacing: 1.08px;
            line-height: 9px;
            color: #33333a;
        `,
    },
};

export interface Props {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    showMoreLimit: number;
    extendedStyles?: FilterTypeAccordionSectionStyles;
    expandByDefault: boolean;
}

const FilterTypeAccordionSection: FC<Props> = ({
    title,
    facets,
    onChangeFacet,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(filterTypeAccordionSectionStyles, extendedStyles));
    const [expanded, setExpanded] = useState(false);

    const limitedFacets = expanded ? facets : facets?.slice(0, showMoreLimit);
    const anySelected = facets.find(f => f.selected) !== undefined;

    return (
        <Accordion headingLevel={1} {...styles.wrapper}>
            <ManagedAccordionSection title={title} {...styles.accordionSection} initialExpanded={expandByDefault}>
                {limitedFacets?.map(facet => (
                    <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                        <Checkbox
                            {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                            checked={facet.selected}
                            onChange={() => onChangeFacet(facet)}
                            data-test-selector={`facetFilter${facet.id}`}
                        >
                            <Typography {...styles.itemName}>{translate(facet.name)}</Typography>
                        </Checkbox>
                    </CheckboxGroup>
                ))}
                {facets &&
                    facets.length > showMoreLimit &&
                    (expanded ? (
                        <Link {...styles.seeLessLink} onClick={() => setExpanded(false)}>
                            {translate("See less")}
                        </Link>
                    ) : (
                        <Link {...styles.seeAllLink} onClick={() => setExpanded(true)}>
                            {translate("See more")}
                        </Link>
                    ))}
            </ManagedAccordionSection>
        </Accordion>
    );
};

export default FilterTypeAccordionSection;
